import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import {
	type IncomingInvoiceReminderLinkSchema,
	incomingInvoiceReminderLinkSchema,
} from "@msuite/utils/schemas";
import type { IncomingInvoiceReminderListItem } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { IncomingInvoiceLinkForm } from "@/forms/incoming-invoice-link-form";
import { client } from "@/hono";

/** Props Interface */
interface LinkReminderModalProps extends ModalProps {
	reminder: IncomingInvoiceReminderListItem;
}

/** Link a reminder to the invoice it reminds of, or — emptied — take a wrong link back. */
export function LinkReminderModal({ isOpen, onClose, reminder }: LinkReminderModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const methods = useForm<IncomingInvoiceReminderLinkSchema>({
		defaultValues: { incomingInvoiceId: reminder.invoice?.id ?? null },
		resolver: resolver(incomingInvoiceReminderLinkSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: IncomingInvoiceReminderLinkSchema) {
		const response = await client.incoming_invoices.reminders[":reminderId"].$patch({
			param: { reminderId: reminder.id },
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["incoming-invoices"]) });
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("incoming_invoices.reminders.link_invoice")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
			>
				<Modal.Body>
					<p className="label">
						{t("incoming_invoices.reminders.link_invoice_hint", {
							number: reminder.referencedInvoiceNumber ?? "–",
						})}
					</p>
					<IncomingInvoiceLinkForm />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
